// These helpers are meant to be used by reducers that handle processing of standard QCS "sync" resources.

import {
  getIn,
  Map as ImmutableMap,
  isIndexed,
  isKeyed,
  OrderedSet,
  Record,
  type RecordOf,
  Set as SetImmutable,
  setIn,
} from 'immutable';
import _isEqualWith from 'lodash/isEqualWith';

import { getLogger } from '../../utils/core';
import { logResourceChange, MAX_CHANGED_KEYS } from './resourceChangeLog';
import type {
  BaseResource,
  QcsSyncResourceStore,
  QcsSyncResourceStoreProps,
  QcsSyncResourcesPayload,
} from './resourceStoreTypes';

const log = getLogger('flux/core/resourceUpserters.ts');

type ResourceMap = ImmutableMap<string, unknown>;

function changedKeys(previous: ResourceMap, next: ResourceMap, resources: ReadonlyArray<BaseResource | undefined>) {
  if (resources.length > MAX_CHANGED_KEYS) return null;
  const keys = new Set<string>();
  // Only keys whose value changed: payloads often name a clientId the map never held.
  const addIfChanged = (key: string | null | undefined) => {
    if (key && previous.get(key) !== next.get(key)) keys.add(key);
  };
  resources.forEach((resource) => {
    addIfChanged(resource?.id);
    addIfChanged(resource?.clientId);
  });
  return [...keys];
}

// Whether the upsert deleted a key, including an optimistic key it inserted earlier in the same batch.
function deletesAnyKey(previous: ResourceMap, resources: ReadonlyArray<BaseResource | undefined>) {
  const inserted = new Set<string>();
  return resources.some((resource) => {
    if (!resource) return false;
    if (resource.isDeleted) return true;
    const { id, clientId } = resource;
    if (id && clientId && (previous.has(clientId) || inserted.has(clientId))) return true;
    const key = id || clientId;
    if (key) inserted.add(key);
    return false;
  });
}

// Logs the upsert so selectors can re-test only the changed records.
function logChange(previous: ResourceMap, next: ResourceMap, resources: ReadonlyArray<BaseResource | undefined>) {
  if (previous === next) return;
  const keys = changedKeys(previous, next, resources);
  // Bulk changes are never applied in place, so they skip the delete scan.
  logResourceChange(previous, next, keys, keys !== null && deletesAnyKey(previous, resources));
}

// mark resource as invalid and save the error
export function completeWithError<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T> = QcsSyncResourceStoreProps<T>,
>(state: QcsSyncResourceStore<T, V>, error: unknown) {
  return state.withMutations((store) => store.set('error', error).set('isLoading', false));
}

// soft reset - keeps valid records and force to update collection
export function resetResources<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T> = QcsSyncResourceStoreProps<T>,
>(state: QcsSyncResourceStore<T, V>, error: unknown) {
  return state.withMutations((store) =>
    store
      .set(
        'resourcesById',
        state.resourcesById.filter((resource, key) => key !== resource.clientId || key === resource.id), // clean optimistic records only
      )
      .set('lastSyncDate', null)
      .set('error', error),
  );
}

// doUpsertResource
//
// Upsert a resource into the resourcesById map.
//
// Returns the updated resourcesById map.
//
function doUpsertResource<T extends BaseResource>(
  resourcesById: QcsSyncResourceStore<T>['resourcesById'],
  resourceToUpsert: RecordOf<T>,
  doMerge = true,
  retainDeleted = false,
): ImmutableMap<string, RecordOf<T>> {
  let updatedResourcesById = resourcesById;
  if (verify(!!resourceToUpsert, 'valid resourceToUpsert is required')) {
    const id = resourceToUpsert.id || resourceToUpsert.clientId;
    if (verify(!!id, 'doUpsertResource: no id found', resourceToUpsert) && id) {
      if (!resourceToUpsert.isDeleted) {
        const existingResource = resourcesById.get(id);
        if (!doMerge || !existingResource) {
          updatedResourcesById = resourcesById.set(id, resourceToUpsert);
        } else {
          updatedResourcesById = resourcesById.set(id, mergeResources(existingResource, resourceToUpsert));
        }
      } else if (retainDeleted) {
        // Keep deleted resources in the map as tombstones so callers can detect
        // stale bump messages. Consumers must filter out isDeleted records.
        const existingResource = resourcesById.get(id);
        if (!doMerge || !existingResource) {
          updatedResourcesById = resourcesById.set(id, resourceToUpsert);
        } else {
          updatedResourcesById = resourcesById.set(id, mergeResources(existingResource, resourceToUpsert));
        }
      } else {
        updatedResourcesById = resourcesById.delete(id);
      }
      if (resourceToUpsert.id && resourceToUpsert.clientId) {
        // try and delete any resource by clientId once we update, safe to delete even if it doesn't exist
        updatedResourcesById = updatedResourcesById.delete(resourceToUpsert.clientId);
      }
    }
  }
  return updatedResourcesById;
}

// A merge needs a record on one side; with plain objects on both, the incoming resource replaces the stored one.
const mergeResources = <T extends BaseResource>(existing: RecordOf<T>, incoming: RecordOf<T>) =>
  mergeDeepEx(existing, incoming) ?? incoming;

type indexOrKey = string | number | symbol | undefined;

export type CompareFunc = ((val: unknown, val2: unknown, key: indexOrKey) => boolean | undefined) | string | undefined; // return true if equal

// by default, treat falsy values as equivalent and ignore 'modifiedAt'
const baseCompare = (val1: unknown, val2: unknown, key: indexOrKey) =>
  key === 'modifiedAt' || (!val1 && !val2) ? true : undefined;
// undefined lets lodash use its internal compare

type ResolvedCompareFunc = Exclude<CompareFunc, string | undefined>;

// strictCompare
//
// Compare every value, including modifiedAt and nested data. Numbers use Object.is so signed zero
// stays distinct; undefined lets lodash use its internal compare for everything else.
export const strictCompare: ResolvedCompareFunc = (val1, val2) =>
  typeof val1 === 'number' && typeof val2 === 'number' ? Object.is(val1, val2) : undefined;

// haveEqualResourceValues
//
// Compare two resources by value with a lodash isEqualWith customizer. Immutable records compare
// through their serialized values so nested collections compare structurally.
export function haveEqualResourceValues<T extends BaseResource>(
  previous: RecordOf<T>,
  incoming: RecordOf<T>,
  comparator: ResolvedCompareFunc,
): boolean {
  const previousJS = previous.toJS ? previous.toJS() : previous;
  const incomingJS = incoming.toJS ? incoming.toJS() : incoming;
  return _isEqualWith(previousJS, incomingJS, comparator);
}

// ================================================================================================
// upsertResource
//
// Optimized to upsert a single resource into the resources store. This method is generally called
// by the reducer after creating/updating/deleting a single resource. In this case, the default
// doMerge setting will doMerge in any changes from the payload sent to QCS. If this method is called
// for replacing a resource (after receiving from QCS), then doMerge should be set to false.
//
// Returns the updated state.
// ================================================================================================
export function upsertResource<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T> = QcsSyncResourceStoreProps<T>,
>(
  state: QcsSyncResourceStore<T, V>,
  resourceToUpsert: RecordOf<T>,
  doMerge = true,
  isLoading = false,
  comparator?: CompareFunc,
  retainDeleted = false,
) {
  if (comparator && resourceToUpsert?.id) {
    const previousResource = state.resourcesById.get(resourceToUpsert.id);
    if (previousResource) {
      // allow using default modifiedAt ignore if 'default' string provided instead of function
      const finalComparator = typeof comparator === 'string' ? comparator === 'default' && baseCompare : comparator;
      if (finalComparator && haveEqualResourceValues(previousResource, resourceToUpsert, finalComparator)) {
        return state;
      }
    }
  }
  const resourcesById = doUpsertResource(state.resourcesById, resourceToUpsert, doMerge, retainDeleted);
  logChange(state.resourcesById, resourcesById, [resourceToUpsert]);
  return state.withMutations((store) => store.set('resourcesById', resourcesById).set('isLoading', isLoading));
}

// upsertResources
//
// Optimized to upsert multiple sync resources into a sync resource store.
//
export function upsertResources<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T> = QcsSyncResourceStoreProps<T>,
>(
  state: QcsSyncResourceStore<T, V>,
  data: QcsSyncResourcesPayload<T>,
  comparator?: CompareFunc,
  retainDeleted = false,
): QcsSyncResourceStore<T, V> {
  const { resources, doMerge = true, replaceCollection = false, lastSyncDate, error = null } = data;

  const resourcesById: ImmutableMap<string, RecordOf<T>> = replaceCollection ? ImmutableMap() : state.resourcesById;

  const setLoaded = (store: QcsSyncResourceStore<T, V>, updatedResourcesById: ImmutableMap<string, RecordOf<T>>) =>
    store
      .set('resourcesById', updatedResourcesById)
      .set('loadPending', false)
      .set('isLoading', false)
      .set('error', error)
      .set('lastSyncDate', lastSyncDate || state.lastSyncDate);

  // optimize when upserting 0 or 1 resource
  //
  if (!resources || resources.length === 0) {
    return state.withMutations((store) => setLoaded(store, resourcesById));
  }
  if (comparator && resources.length <= 4) {
    const finalComparator = typeof comparator === 'string' ? comparator === 'default' && baseCompare : comparator;
    if (finalComparator) {
      // look for the first 'newData'
      const newDataIndex = resources.findIndex((resource) => {
        const prevResource = resource.id ? resourcesById.get(resource.id) : undefined;
        return !prevResource || !haveEqualResourceValues(prevResource, resource, finalComparator);
      });

      if (newDataIndex === -1) {
        // did not find any new data
        return state.withMutations((store) => setLoaded(store, resourcesById)); // keeping existing resources
      }
    }
  }

  const updatedResourcesById =
    resources.length === 1
      ? doUpsertResource(resourcesById, resources[0], doMerge, retainDeleted)
      : resourcesById.withMutations((resourcesByIdMutable) => {
          resources.forEach((resource) => {
            doUpsertResource(resourcesByIdMutable, resource, doMerge, retainDeleted);
          });
        });
  // A replaced collection is not derived from the previous map.
  if (!replaceCollection) logChange(state.resourcesById, updatedResourcesById, resources);
  return state.withMutations((store) => setLoaded(store, updatedResourcesById));
}

// dropUnchangedResources
//
// Remove payload rows that upsertResources would merge into a value-equal record, so the existing
// record identities (and every selector cache keyed on them) survive a refresh that returns the same
// data. Upserts are sequential: repeated ids and optimistic-key cleanup can affect later rows, so the
// payload is returned unchanged whenever comparing rows against the initial map would be unsafe.
// Rows without an id, deleted rows, new rows, rows of another record type and changed rows are kept.
export function dropUnchangedResources<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T> = QcsSyncResourceStoreProps<T>,
>(
  state: QcsSyncResourceStore<T, V>,
  payload: QcsSyncResourcesPayload<T>,
  comparator: ResolvedCompareFunc = strictCompare,
): QcsSyncResourcesPayload<T> {
  if (payload.replaceCollection || !payload.resources?.length) return payload;

  const keys = new Set<string>();
  const cleanupClientIds = new Set<string>();
  for (const incoming of payload.resources) {
    const { id, clientId } = incoming;
    const key = id || clientId;
    if (key && (keys.has(key) || cleanupClientIds.has(key))) return payload;
    if (key) keys.add(key);
    if (id && clientId) {
      if (keys.has(clientId) || state.resourcesById.has(clientId)) return payload;
      // Remember cleanup targets so collisions with later payload keys are caught in the same pass.
      cleanupClientIds.add(clientId);
    }
  }

  const resources = payload.resources.filter((incoming) => {
    if (!incoming.id || incoming.isDeleted) return true;
    const previous = state.resourcesById.get(incoming.id);
    return (
      !previous ||
      previous.constructor !== incoming.constructor ||
      !haveEqualResourceValues(previous, incoming, comparator)
    );
  });
  if (resources.length === payload.resources.length) return payload;
  return 'set' in payload ? payload.set('resources', resources) : { ...payload, resources };
}

function resolveConflictAndContinue<C>(curObj: C, key: unknown, value: unknown, newObj: unknown, basePath: unknown[]) {
  let retObj = curObj;

  // setIn with a one-key path sets that key on any record, collection or plain object.
  const path = [...basePath, key];
  if (Array.isArray(value) || isIndexed(value) || SetImmutable.isSet(value) || OrderedSet.isOrderedSet(value)) {
    // erase arrays or simple collections
    const newValue = getIn(newObj, path);
    if (newValue) {
      try {
        retObj = setIn(retObj, [key], null);
      } catch (e) {
        log.error(e);
      }
    }
  } else if (typeof value === 'object' && value !== null && key) {
    const retValue = resolveConflicts(value, newObj, path);
    try {
      retObj = setIn(retObj, [key], retValue);
    } catch (e) {
      log.error(e);
    }
  }

  return retObj;
}

function resolveConflicts<C>(curObj: C, newObj: unknown, basePath: unknown[] = []): C {
  let retObj = curObj;

  if ((Record.isRecord(curObj) || isKeyed(curObj)) && curObj[Symbol.iterator]) {
    // iterate over keyed immutables
    for (const prop of curObj) {
      if (Array.isArray(prop) && prop.length === 2) {
        const [key, value] = prop;
        retObj = resolveConflictAndContinue(retObj, key, value, newObj, basePath);
      }
    }
  } else if (typeof curObj === 'object' && curObj !== null) {
    // iterate over js objects
    for (const key in curObj) {
      const value = curObj[key];
      retObj = resolveConflictAndContinue(retObj, key, value, newObj, basePath);
    }
  }

  return retObj;
}

export function mergeDeepEx<T extends object>(oldObj: T, newObj: T): T | undefined;
/** Merging fields into a record gives a record. Pass `T`, since it is not inferred from a record. */
export function mergeDeepEx<T extends object>(oldObj: RecordOf<T>, newObj: Partial<T>): RecordOf<T>;
export function mergeDeepEx(oldObj: object, newObj: object): object | undefined {
  const oldObjResolved = resolveConflicts(oldObj, newObj);
  if (Record.isRecord(newObj)) {
    // if new record, merge into new record in case record types/fields updated
    return newObj.mergeDeepWith((newValue, oldValue) => (newValue === undefined ? oldValue : newValue), oldObjResolved);
  }
  if (Record.isRecord(oldObjResolved)) {
    // merge JS obj into current record
    return oldObjResolved.mergeDeepWith((oldValue, newValue) => (newValue === undefined ? oldValue : newValue), newObj);
  }
  return undefined;
}
